import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Table, Form, Spinner, Tabs, Tab } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import Dollars from "../../common/typeFormatting/Dollars";
import "./ManagerStyles.css";
import { getTravelTotals } from "./api/TravelManagerApi";

const TravelManagerDashboard = ({ profile }) => {
  // State management
  const [rawData, setRawData] = useState([]); // Original data from API
  const [isLoading, setIsLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [groupBy, setGroupBy] = useState("directIndirect"); // Default to Direct/Indirect
  const [sortBy, setSortBy] = useState("lastName"); // Default sort by lastName
  const [sortDirection, setSortDirection] = useState("asc"); // Default ascending
  const [processedData, setProcessedData] = useState([]); // Processed data after filtering/grouping/sorting
  
  const [activeAllowanceType, setActiveAllowanceType] = useState("TRAVEL");
  const history = useHistory();

  // Fetch data from API whenever the allowance type changes
  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const teamTravelData = await getTravelTotals(
          activeAllowanceType // Pass the active allowance type to the API
        );
        setRawData(teamTravelData);
      } catch (error) {
        console.error(`Error fetching team ${activeAllowanceType} allowance data:`, error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [profile.employeeId, profile.isImpersonation, profile.dataEmployeeId, activeAllowanceType]);

  // Process data whenever any parameters change
  useEffect(() => {
    if (rawData.length === 0) return;

    // Step 1: Filter data based on search text (by lastName only with startsWith)
    const filtered = rawData.filter(employee => {
      const searchText = filterText.toLowerCase();
      return employee.lastName.toLowerCase().startsWith(searchText);
    });

    // Step 2: Create groups based on groupBy
    const groups = {};
    
    if (groupBy === "none") {
      // If no grouping, put all in one group
      groups["All Employees"] = {
        title: "All Employees",
        employees: [...filtered]
      };
    } else if (groupBy === "directIndirect") {
      // If it's direct/indirect, also group indirect reports by their managers
      if (filtered.some(employee => employee.isDirectReport)) {
        groups["direct_reports"] = {
          title: "Direct Reports",
          employees: []
        };
      }
      
      filtered.forEach(employee => {
        if (employee.isDirectReport) {
          groups["direct_reports"].employees.push(employee);
        } else {
          // For indirect reports, group by their immediate manager
          const managerName = `${employee.immediate_Manager_FirstName || ''} ${employee.immediate_Manager_LastName || ''}`.trim();
          const groupKey = managerName || "Unknown Manager";
          
          if (!groups[groupKey]) {
            groups[groupKey] = {
              title: managerName || "Unknown Manager",
              employees: []
            };
          }
          
          groups[groupKey].employees.push(employee);
        }
      });
    } else if (groupBy === "department") {
      // Group by department
      filtered.forEach(employee => {
        const department = employee.department || "No Department";
        
        if (!groups[department]) {
          groups[department] = {
            title: department,
            employees: []
          };
        }
        
        groups[department].employees.push(employee);
      });
    } else if (groupBy === "status") {
      // Group by status
      filtered.forEach(employee => {
        const status = employee.employeeStatus || "Unknown Status";
        
        if (!groups[status]) {
          groups[status] = {
            title: status,
            employees: []
          };
        }
        
        groups[status].employees.push(employee);
      });
    }
    
    // Step 3: Calculate Travel Allowance totals and sort within each group
    Object.keys(groups).forEach(key => {
      // Calculate the total allowance for this group
      let totalAllowance = 0;
      groups[key].employees.forEach(employee => {
        if (typeof employee.allowanceBalance === 'number') {
          totalAllowance += employee.allowanceBalance;
        }
      });
      
      // Add the total to the group
      groups[key].totalAllowance = totalAllowance;
      
      // Sort employees in this group
      groups[key].employees.sort((a, b) => {
        if (sortBy === "lastName") {
          const compareResult = a.lastName.localeCompare(b.lastName);
          return sortDirection === "asc" ? compareResult : -compareResult;
        } else if (sortBy === "allowanceBalance") {
          // Handle possible null/undefined values
          const aValue = typeof a.allowanceBalance === 'number' ? a.allowanceBalance : 0;
          const bValue = typeof b.allowanceBalance === 'number' ? b.allowanceBalance : 0;
          return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        }
        return 0;
      });
    });
    
    // Step 4: Create array of group objects with specific order
    const orderedGroups = [];
    
    if (groupBy === "none") {
      // Just one group for no grouping
      orderedGroups.push(groups["All Employees"]);
    } else if (groupBy === "directIndirect") {
      // Direct reports first, then other managers alphabetically
      if (groups["direct_reports"]) {
        orderedGroups.push(groups["direct_reports"]);
      }
      
      // Sort all other managers by name
      const otherManagerKeys = Object.keys(groups)
        .filter(key => key !== "direct_reports" && key !== "All Employees")
        .sort();
      
      otherManagerKeys.forEach(key => {
        orderedGroups.push(groups[key]);
      });
    } else if (groupBy === "status") {
      // Custom order for status groups: Active, Inactive, Terminated, then others alphabetically
      const priorityOrder = ["Active", "Inactive", "Terminated"];
      
      // First add the priority groups in specified order
      priorityOrder.forEach(status => {
        if (groups[status]) {
          orderedGroups.push(groups[status]);
        }
      });
      
      // Then add all other groups alphabetically
      const otherStatuses = Object.keys(groups)
        .filter(status => !priorityOrder.includes(status))
        .sort();
      
      otherStatuses.forEach(status => {
        orderedGroups.push(groups[status]);
      });
    } else {
      // Alphabetical order for departments
      const departmentKeys = Object.keys(groups).sort();
      departmentKeys.forEach(key => {
        orderedGroups.push(groups[key]);
      });
    }
    
    // Step 5: Update processed data state
    setProcessedData(orderedGroups);
    
  }, [rawData, filterText, groupBy, sortBy, sortDirection]);

  // Event handlers
  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
  };

  const handleGroupChange = (e) => {
    setGroupBy(e.target.value);
  };
  
  const handleColumnHeaderClick = (column) => {
    if (sortBy === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column as sort field and reset to ascending
      setSortBy(column);
      setSortDirection("asc");
    }
  };
  
  const handleRowClick = (employee) => {
    history.push(`/travel/manager/employee/${employee.employeeId}`, { 
      selectedEmployee: employee 
    });
  };

  // Add handler for tab change
  const handleTabChange = (tabKey) => {
    setActiveAllowanceType(tabKey);
    // Reset processed data when switching tabs
    setProcessedData([]);
  };

  // Helper function to get the title for the allowance type
  const getAllowanceTypeTitle = (type) => {
    switch (type) {
      case "TRAVEL":
        return "Travel Allowance";
      case "PARTTECH":
        return "Partner Tech Allowance";
      default:
        return "Allowance";
    }
  };

  // CSS for sortable headers
  const sortableHeaderStyle = {
    cursor: 'pointer',
    userSelect: 'none',
  };

  // Add custom styles for the tabs
  const tabContentStyle = {
    backgroundColor: 'white',
    borderLeft: '1px solid #dee2e6',
    borderRight: '1px solid #dee2e6',
    borderBottom: '1px solid #dee2e6',
    borderRadius: '0 0 0.25rem 0.25rem',
    padding: '15px'
  };

  return (
    <Container fluid>
      <div className="mt-3">
        <Tabs
          activeKey={activeAllowanceType}
          onSelect={handleTabChange}
          className="mb-0"
          id="allowance-type-tabs"
        >
          <Tab eventKey="TRAVEL" title="Travel Allowance" />
          <Tab eventKey="PARTTECH" title="Partner Tech Allowance" />
        </Tabs>
        
        <div style={tabContentStyle}>
          <Row className="mb-3 align-items-center">
            <Col md={6}>
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Search by last name..."
                  value={filterText}
                  onChange={handleFilterChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Control as="select" value={groupBy} onChange={handleGroupChange}>
                  <option value="none">No Grouping</option>
                  <option value="directIndirect">Group by Direct/Indirect</option>
                  <option value="department">Group by Department</option>
                  <option value="status">Group by Status</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          
          <Row>
            <Col>
              {isLoading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" />
                  <p className="mt-3">Loading {getAllowanceTypeTitle(activeAllowanceType)} data...</p>
                </div>
              ) : (
                <>
                  {processedData.length > 0 ? (
                    processedData.map((group, index) => (
                      <Card className="mb-4" key={index}>
                        <Card.Header>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{group.title}</strong> ({group.employees.length} {group.employees.length === 1 ? 'employee' : 'employees'})
                            </div>
                            <div>
                              <strong>Total {getAllowanceTypeTitle(activeAllowanceType)}: </strong>
                              <Dollars>{group.totalAllowance}</Dollars>
                            </div>
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <Table hover responsive>
                            <thead>
                              <tr>
                                <th 
                                  onClick={() => handleColumnHeaderClick("lastName")}
                                  style={{
                                    ...sortableHeaderStyle,
                                    ...(sortBy === "lastName" ? {fontWeight: 'bold', fontStyle: 'italic'} : {})
                                  }}
                                >
                                  Last Name {sortBy === "lastName" && (sortDirection === "asc" ? "↑" : "↓")}
                                </th>
                                <th>First Name</th>
                                <th>Department</th>
                                <th>Status</th>
                                <th 
                                  onClick={() => handleColumnHeaderClick("allowanceBalance")}
                                  style={{
                                    ...sortableHeaderStyle, 
                                    textAlign: 'right',
                                    ...(sortBy === "allowanceBalance" ? {fontWeight: 'bold', fontStyle: 'italic'} : {})
                                  }}
                                >
                                  {getAllowanceTypeTitle(activeAllowanceType)} Balance {sortBy === "allowanceBalance" && (sortDirection === "asc" ? "↑" : "↓")}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.employees.map((employee) => (
                                <tr 
                                  key={employee.employeeId}
                                  onClick={() => handleRowClick(employee)}
                                  style={{ cursor: 'pointer' }}
                                  className="hover-highlight"
                                >
                                  <td>{employee.lastName}</td>
                                  <td>{employee.firstName}</td>
                                  <td>{employee.department || "-"}</td>
                                  <td>{employee.employeeStatus || "-"}</td>
                                  <td className="text-right">
                                    <Dollars>{employee.allowanceBalance}</Dollars>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </Card.Body>
                      </Card>
                    ))
                  ) : (
                    <Card>
                      <Card.Body className="text-center">
                        <p>No matching employees found</p>
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}
            </Col>
          </Row>
        </div>
      </div>
    </Container>
  );
};

export default TravelManagerDashboard;